import React, { useState, useEffect } from "react";
import { Icon } from "@iconify/react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../Context/authContext";
import { Typography } from "../../Components/Typography";
import Card from "../../Components/Card";
import { PieChart, Pie, Cell, ResponsiveContainer } from "recharts";
import Avatar from "../../Components/Avatar";
import { getEventsByRole } from "../../Utils/apiServices";

const getDateString = () => {
  return new Date().toLocaleDateString(undefined, {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
};

// Donut Chart Component using Recharts
const DonutChart = ({ data }) => {
  const colors = {
    Spark: "#3B82F6", // Blue
    Shape: "#A78BFA", // Light purple
    Structure: "#EC4899", // Pink
  };

  const COLORS = data.map((item) => colors[item.label] || "#94A3B8");

  return (
    <div className="flex items-center gap-6">
      <div className="relative w-32 h-32 flex-shrink-0">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={data}
              cx="50%"
              cy="50%"
              labelLine={false}
              outerRadius={40}
              innerRadius={25}
              fill="#8884d8"
              dataKey="value"
              startAngle={90}
              endAngle={-270}
            >
              {data.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={COLORS[index]} />
              ))}
            </Pie>
          </PieChart>
        </ResponsiveContainer>
      </div>
      <div className="flex flex-col gap-2">
        {data.map((item, index) => (
          <div key={index} className="flex items-center gap-2">
            <div
              className="w-3 h-3 rounded-full flex-shrink-0"
              style={{ backgroundColor: colors[item.label] || "#94A3B8" }}
            />
            <Typography as="p" className="text-sm text-slate-700">
              {item.label}
            </Typography>
            <Typography
              as="p"
              className="text-sm font-semibold text-slate-900 ml-auto"
            >
              {item.value}
            </Typography>
          </div>
        ))}
      </div>
    </div>
  );
};

const MentorDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [eventsCount, setEventsCount] = useState(0);

  const getDisplayName = () => {
    if (user?.first_name || user?.last_name) {
      return (
        `${user?.first_name || ""} ${user?.last_name || ""}`.trim() ||
        user?.email ||
        "Guest"
      );
    }
    return user?.email || "Guest";
  };

  useEffect(() => {
    fetchEventsCount();
  }, []);

  const fetchEventsCount = async () => {
    try {
      const response = await getEventsByRole("mentor");
      if (response.success && response?.data?.data) {
        const events = Array.isArray(response.data?.data) ? response.data?.data : [];
        
        // Filter events for the next 30 days
        const now = new Date();
        const thirtyDaysFromNow = new Date();
        thirtyDaysFromNow.setDate(now.getDate() + 30);
        
        const upcomingEvents = events.filter((event) => {
          if (!event.start_at) return false;
          const eventStartDate = new Date(event.start_at);
          return eventStartDate >= now && eventStartDate <= thirtyDaysFromNow;
        });
        
        setEventsCount(upcomingEvents.length);
      }
    } catch (error) {
      console.error("Error fetching events count:", error);
    }
  };

  const engagementsData = [
    { label: "Spark", value: 0 },
    { label: "Shape", value: 0 },
    { label: "Structure", value: 0 },
  ];

  const statsCards = [
    {
      id: 1,
      title: "Startups Mentored",
      value: 0,
      footerText: "This Week",
      icon: "lucide:zap",
      iconBg: "bg-brand-secondary",
      iconColor: "text-brand-icon",
      rowSpan: "row-span-1",
      colSpan: "",
      showChart: false,
    },
    {
      id: 2,
      title: "Pending Meetings",
      value: 0,
      footerText: "This Week",
      icon: "lucide:zap",
      iconBg: "bg-brand-secondary",
      iconColor: "text-brand-icon",
      rowSpan: "row-span-1",
      colSpan: "",
      showChart: false,
    },
    {
      id: 3,
      title: "Total Engagements at Each Stage",
      value: null,
      footerText: "Current",
      icon: null,
      iconBg: null,
      iconColor: null,
      rowSpan: "row-span-2",
      colSpan: "md:col-span-2 lg:col-span-1",
      showChart: true,
      chartData: engagementsData,
      footerAction: {
        label: "View Engagements",
        onClick: () => navigate("/engagements"),
        icon: "lucide:arrow-right",
      },
    },
    {
      id: 4,
      title: "Active Ventures",
      value: 0,
      footerText: "Current",
      icon: "lucide:zap",
      iconBg: "bg-brand-secondary",
      iconColor: "text-brand-icon",
      rowSpan: "row-span-1",
      colSpan: "",
      showChart: false,
    },
    {
      id: 5,
      title: "Tasks Due",
      value: 0,
      footerText: "This Week",
      icon: "lucide:zap",
      iconBg: "bg-brand-secondary",
      iconColor: "text-brand-icon",
      rowSpan: "row-span-1",
      colSpan: "",
      showChart: false,
    },
  ];

  const bottomCards = [
    {
      id: 6,
      title: "New Idea Pending Review",
      value: 0,
      footerText: "Current",
      icon: "lucide:alert-circle",
      iconBg: "bg-warning-secondary",
      iconColor: "text-[#252B37]",
      footerAction: {
        label: "Review Ideas",
        onClick: () => navigate("/ideas-review"),
        icon: "lucide:arrow-right",
      },
    },
    {
      id: 7,
      title: "Events",
      value: eventsCount,
      footerText: "Next 30 Days",
      icon: "lucide:alert-circle",
      iconBg: "bg-warning-secondary",
      iconColor: "text-[#252B37]",
      footerAction: {
        label: "View Events",
        onClick: () => navigate("/mentor-events"),
        icon: "lucide:arrow-right",
      },
    },
  ];

  return (
    <section className="flex flex-col w-full h-auto rounded-[40px] pt-8 pb-12 gap-8 bg-primary">
      {/* Header */}
      <div className="flex w-auto h-auto px-8 items-start justify-start gap-4">
        <Avatar
          src={user?.profile_picture_url}
          firstName={user?.first_name}
          lastName={user?.last_name}
          email={user?.email}
          alt="User avatar"
          size="w-16 h-16"
          showBorder={true}
          className="border-slate-200"
        />
        <div className="flex flex-col gap-1 items-start justify-start">
          <Typography
            as="h1"
            className="text-xl font-semibold text-primary-900"
          >
            Hello, {getDisplayName()}
          </Typography>
          <Typography as="p" className="text-sm text-tertiary-600">
            {getDateString()}
          </Typography>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="w-auto h-auto px-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 grid-rows-2 gap-4">
        {statsCards.map((card) => (
          <Card
            key={card.id}
            className={`flex flex-col rounded-2xl shadow-sm ${card.rowSpan} ${card.colSpan} items-start justify-between`}
            bodyClassName={card.showChart ? "p-6 flex flex-col gap-4" : "p-6 flex flex-col gap-3"}
            headerClassName="!w-full !flex !items-center !justify-start !gap-2 !border-b-0 !border-b-transparent"
            header={
              <>
                {card.icon && (
                  <div className={`h-12 w-12 rounded-full ${card.iconBg} flex items-center justify-center`}>
                    <Icon icon={card.icon} className={`h-6 w-6 ${card.iconColor}`} />
                  </div>
                )}
                <Typography
                  as="p"
                  className="text-md font-medium text-primary-900"
                >
                  {card.title}
                </Typography>
              </>
            }
            footerClassName="!w-full !flex !items-end !justify-between !gap-2 !border-t-0 border-t-transparent !bg-white"
            footer={
              <>
                <Typography as="p" className={card.showChart ? "text-xs text-[#475467] font-medium" : "text-sm text-[#475467] font-medium"}>
                  {card.footerText}
                </Typography>
                {card.footerAction && (
                  <button
                    onClick={card.footerAction.onClick}
                    className="text-sm text-purple-600 hover:text-purple-700 font-medium flex items-center gap-1"
                  >
                    {card.footerAction.label}
                    <Icon icon={card.footerAction.icon} className="h-4 w-4" />
                  </button>
                )}
              </>
            }
          >
            {card.showChart ? (
              <DonutChart data={card.chartData} />
            ) : (
              <div className="flex flex-col gap-5 items-start justify-between">
                <Typography
                  as="h3"
                  className="text-3xl font-semibold text-primary-900"
                >
                  {card.value}
                </Typography>
              </div>
            )}
          </Card>
        ))}
      </div>

      {/* Bottom Cards Section */}
      <div className="flex w-auto h-auto px-8 gap-4">
        {bottomCards.map((card) => (
          <Card
            key={card.id}
            className="flex flex-col w-full rounded-2xl shadow-sm items-start justify-between"
            bodyClassName="p-6 flex items-center justify-between"
            headerClassName="!w-full !flex !items-center !justify-start !gap-2 !border-b-0 !border-b-transparent"
            header={
              <>
                <div className={`h-12 w-12 rounded-full ${card.iconBg} flex items-center justify-center`}>
                  <Icon
                    icon={card.icon}
                    className={`h-6 w-6 ${card.iconColor}`}
                  />
                </div>
                <Typography
                  as="p"
                  className="text-md font-medium text-primary-900"
                >
                  {card.title}
                </Typography>
              </>
            }
            footerClassName="!w-full !flex !items-end !justify-between !gap-2 !border-t-0 border-t-transparent !bg-white"
            footer={
              <>
                <Typography as="p" className="text-sm text-[#475467] font-medium">
                  {card.footerText}
                </Typography>
                {card.footerAction && (
                  <button
                    onClick={card.footerAction.onClick}
                    className="text-sm text-purple-600 hover:text-purple-700 font-medium flex items-center gap-1"
                  >
                    {card.footerAction.label}
                    <Icon icon={card.footerAction.icon} className="h-4 w-4" />
                  </button>
                )}
              </>
            }
          >
            <div className="flex flex-col gap-5 items-start justify-between">
              <Typography
                as="h3"
                className="text-3xl font-semibold text-primary-900"
              >
                {card.value}
              </Typography>
            </div>
          </Card>
        ))}
      </div>
    </section>
  );
};

export default MentorDashboard;
